import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { QUERY_KEYS } from "@/lib/constants";
import type {
  User,
  CreateUserRequest,
  UpdateUserRequest,
  ChangePasswordRequest,
  ChangeOwnPasswordRequest,
  ChangePinRequest,
  RemovePinRequest,
  SetPinRequest,
  Role,
  Permission,
  CreateRoleRequest,
  UpdateRoleRequest,
  SetRolePermissionsRequest,
  PaginatedResponse,
} from "@/types";

export function useCurrentUser() {
  return useQuery({
    queryKey: QUERY_KEYS.currentUser,
    queryFn: () => apiClient.get<User>("/auth/me"),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
}

interface UserFilters {
  search?: string;
  status?: "all" | "active" | "inactive";
  page?: number;
  limit?: number;
}

export function useUsers(params?: UserFilters) {
  return useQuery({
    queryKey: [...QUERY_KEYS.users, params],
    queryFn: () =>
      apiClient.get<PaginatedResponse<User>>("/auth/users", {
        search: params?.search,
        status: params?.status,
        page: params?.page,
        limit: params?.limit,
      }),
    placeholderData: (prev) => prev,
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.user(id),
    queryFn: () => apiClient.get<User>(`/auth/users/${id}`),
    enabled: !!id,
  });
}

export function useRoles(includePermissions = false) {
  return useQuery({
    queryKey: includePermissions
      ? [...QUERY_KEYS.roles, "withPermissions"]
      : QUERY_KEYS.roles,
    queryFn: () =>
      apiClient.get<Role[]>("/auth/roles", {
        includePermissions: includePermissions ? "true" : undefined,
      }),
  });
}

export function useRole(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.role(id),
    queryFn: () => apiClient.get<Role>(`/auth/roles/${id}`),
    enabled: !!id,
  });
}

export function useAllPermissions() {
  return useQuery({
    queryKey: QUERY_KEYS.permissions,
    queryFn: () => apiClient.get<Permission[]>("/auth/permissions"),
    staleTime: 10 * 60 * 1000,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserRequest) =>
      apiClient.post<User>("/auth/users", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
      apiClient.put<User>(`/auth/users/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user(id) });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ChangePasswordRequest }) =>
      apiClient.put(`/auth/users/${id}/password`, data),
  });
}

export function useChangeOwnPassword() {
  return useMutation({
    mutationFn: (data: ChangeOwnPasswordRequest) =>
      apiClient.put("/auth/me/password", data),
  });
}

export function useChangeOwnPin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChangePinRequest) => apiClient.put("/auth/me/pin", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentUser });
    },
  });
}

export function useRemoveOwnPin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RemovePinRequest) =>
      apiClient.delete("/auth/me/pin", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentUser });
    },
  });
}

export function useSetUserPin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SetPinRequest }) =>
      apiClient.put(`/auth/users/${id}/pin`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user(id) });
    },
  });
}

export function useResetPinAttempts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`/auth/users/${id}/pin/reset-attempts`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user(id) });
    },
  });
}

export function useToggleUserActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiClient.put<User>(`/auth/users/${id}`, { isActive }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user(id) });
    },
  });
}

function invalidateRoleQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.roles });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentUser });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
}

export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRoleRequest) =>
      apiClient.post<Role>("/auth/roles", data),
    onSuccess: () => invalidateRoleQueries(queryClient),
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleRequest }) =>
      apiClient.put<Role>(`/auth/roles/${id}`, data),
    onSuccess: (_, { id }) => {
      invalidateRoleQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.role(id) });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/auth/roles/${id}`),
    onSuccess: () => invalidateRoleQueries(queryClient),
  });
}

export function useSetRolePermissions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: SetRolePermissionsRequest;
    }) => apiClient.put<Role>(`/auth/roles/${id}/permissions`, data),
    onSuccess: (_, { id }) => {
      invalidateRoleQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.role(id) });
    },
  });
}
